import React, { useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Pagination,
  Chip,
} from "@mui/material";
import ProductModal from "./ProductModal";
import { fetchProductData } from "../../api/service";
import { useQuery } from "@tanstack/react-query";
import { HOST_URL } from "../../config/Url";

const ProductGrid = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 12;

  // Using useQuery with the correct parameters
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["products", page],
    queryFn: () => fetchProductData({ page, pageSize }),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        height="400px"
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{ color: "#11A193", mb: 3 }}
        />
        <Typography variant="body1" color="textSecondary">
          Loading products...
        </Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        textAlign="center"
        color="error.main"
        py={6}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography variant="h5" gutterBottom>
          Error loading products
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
          {error?.message || "Failed to load products"}
        </Typography>
      </Box>
    );
  }

  const { products, totalPages, totalItems, total } = data;

  const handleOpenModal = (product) => {
    setSelectedProduct(product);
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
  };

  // Helper function to get product image URL or return null if no valid image
  const getProductImageUrl = (product) => {
    if (!product.fileResources || product.fileResources.length === 0) {
      return null;
    }

    // Inline isImageFile function since the import is failing
    const isImageFile = (file) => {
      if (!file) return false;
      const imageTypes = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];
      return file.fileType && imageTypes.includes(file.fileType.toLowerCase());
    };

    // Filter for valid image files
    const imageFiles = product.fileResources.filter(isImageFile);

    // Return URL if valid image file exists, otherwise null
    return imageFiles.length > 0
      ? `${HOST_URL}/${imageFiles[0].filePath}`
      : null;
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
          pb: 1,
          borderBottom: "1px solid rgba(0,0,0,0.1)",
        }}
      >
        <Typography variant="h4" component="h1">
          Our Products
        </Typography>
        <Chip
          label={`${total || 0} Products`}
          sx={{
            bgcolor: "rgba(17, 161, 147, 0.1)",
            color: "#11A193",
            fontWeight: "bold",
          }}
        />
      </Box>
      <Typography
        variant="body1"
        color="textSecondary"
        sx={{ mb: 4, maxWidth: "800px", mx: "auto", textAlign: "center" }}
      >
        Explore our diverse range of high-quality pharmaceutical products
        designed to meet your health needs.
      </Typography>

      {/* Products Grid */}
      <Grid container spacing={3}>
        {products &&
          products.length > 0 &&
          products.map((product) => (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  borderRadius: "12px",
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 12px 20px rgba(0,0,0,0.1)",
                  },
                }}
                onClick={() => handleOpenModal(product)}
              >
                <Box
                  sx={{
                    height: 200,
                    overflow: "hidden",
                    position: "relative",
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {getProductImageUrl(product) ? (
                    <img
                      src={getProductImageUrl(product)}
                      alt={product.brandName}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.5s ease",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.parentNode.innerHTML =
                          '<div style="display:flex;justify-content:center;align-items:center;height:100%;width:100%;"><p>No Image</p></div>';
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No Image
                    </Typography>
                  )}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      background:
                        "linear-gradient(0deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 50%)",
                      opacity: 0,
                      transition: "opacity 0.3s ease",
                      "&:hover": {
                        opacity: 1,
                      },
                    }}
                  />
                </Box>
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  <Typography
                    gutterBottom
                    variant="h6"
                    component="h2"
                    sx={{
                      fontSize: "1.1rem",
                      fontWeight: "bold",
                      color: "#014b3b",
                      mb: 0.5,
                    }}
                  >
                    {product.brandName}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontSize: "0.875rem",
                      mb: 1,
                    }}
                  >
                    {product.genericName}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mt: "auto",
                      pt: 1,
                      borderTop: "1px dashed rgba(0,0,0,0.1)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#11A193",
                        fontWeight: 500,
                      }}
                    >
                      View Details
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 4 }}>
          <Pagination
            count={totalPages || 1}
            page={page}
            onChange={handlePageChange}
            size="large"
            showFirstButton
            showLastButton
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#014b3b",
              },
              "& .Mui-selected": {
                backgroundColor: "#11A193 !important",
                color: "white !important",
                fontWeight: "bold",
              },
              "& .MuiPaginationItem-page:hover": {
                backgroundColor: "rgba(17, 161, 147, 0.2)",
              },
              "& .MuiPaginationItem-firstLast, & .MuiPaginationItem-previousNext":
                {
                  color: "#014b3b",
                },
            }}
          />
        </Box>
      )}
      <ProductModal product={selectedProduct} handleClose={handleCloseModal} />
    </Box>
  );
};

export default ProductGrid;
